import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Grid, Chip, IconButton,
  Checkbox, TextField, Alert, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, useTheme, alpha,
  Accordion, AccordionSummary, AccordionDetails,
  Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  VpnKey as KDMIcon,
  CheckCircle as DoneIcon,
  Schedule as PendingIcon,
  Error as FailIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Theaters as TheaterIcon,
  Tv as ScreenIcon,
  SelectAll as SelectAllIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { fetchDCPJobs } from '../store/slices/dcpJobsSlice';
import { fetchVenues } from '../store/slices/venuesSlice';
import { generateBulkKDM, fetchKDMRecords, downloadKDM, deleteKDMRecord, emailKDM } from '../store/slices/kdmSlice';
import api from '../services/api';
import { physicalStatus, driveSummaryLabel } from '../utils/physicalDelivery';

const TIMEZONE_OPTIONS = [
  { label: 'Los Angeles (PT)', value: 'America/Los_Angeles' },
  { label: 'Denver (MT)', value: 'America/Denver' },
  { label: 'Chicago (CT)', value: 'America/Chicago' },
  { label: 'New York (ET)', value: 'America/New_York' },
  { label: 'Toronto (ET)', value: 'America/Toronto' },
  { label: 'Mexico City (CST)', value: 'America/Mexico_City' },
  { label: 'São Paulo (BRT)', value: 'America/Sao_Paulo' },
  { label: 'London (GMT/BST)', value: 'Europe/London' },
  { label: 'Paris (CET)', value: 'Europe/Paris' },
  { label: 'Berlin (CET)', value: 'Europe/Berlin' },
  { label: 'Rome (CET)', value: 'Europe/Rome' },
  { label: 'Moscow (MSK)', value: 'Europe/Moscow' },
  { label: 'Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'Mumbai (IST)', value: 'Asia/Kolkata' },
  { label: 'Bangkok (ICT)', value: 'Asia/Bangkok' },
  { label: 'Singapore (SGT)', value: 'Asia/Singapore' },
  { label: 'Hong Kong (HKT)', value: 'Asia/Hong_Kong' },
  { label: 'Shanghai (CST)', value: 'Asia/Shanghai' },
  { label: 'Seoul (KST)', value: 'Asia/Seoul' },
  { label: 'Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Sydney (AEST)', value: 'Australia/Sydney' },
  { label: 'Auckland (NZST)', value: 'Pacific/Auckland' },
  { label: 'Honolulu (HST)', value: 'Pacific/Honolulu' },
];

function offsetDate(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  // Use local time for datetime-local inputs (not UTC)
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Local YYYY-MM-DD for <input type="date"> values.
function offsetDateOnly(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Apply the KDM-window policy (2 weeks before first screening through
// 2 days after last screening). Inputs are YYYY-MM-DD strings from the
// <input type="date"> control; output is { validFrom, validTo } as
// datetime-local strings anchored to local midnight / 23:59. Mirrors
// services/kdm_window_policy.compute_window on the backend.
const DAYS_BEFORE_FIRST_SCREENING = 14;
const DAYS_AFTER_LAST_SCREENING = 2;

function deriveWindow(screeningFirst, screeningLast) {
  if (!screeningFirst) return { validFrom: '', validTo: '' };
  const lastStr = screeningLast || screeningFirst;
  const first = new Date(`${screeningFirst}T00:00:00`);
  const last = new Date(`${lastStr}T23:59:00`);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) {
    return { validFrom: '', validTo: '' };
  }
  const validFromDate = new Date(first);
  validFromDate.setDate(validFromDate.getDate() - DAYS_BEFORE_FIRST_SCREENING);
  const validToDate = new Date(last);
  validToDate.setDate(validToDate.getDate() + DAYS_AFTER_LAST_SCREENING);
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return { validFrom: fmt(validFromDate), validTo: fmt(validToDate) };
}

function DCPDistribute() {
  const { jobId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const { jobs } = useSelector((state) => state.dcpJobs);
  const { venues } = useSelector((state) => state.venues);
  const { records } = useSelector((state) => state.kdm);

  const [selectedScreens, setSelectedScreens] = useState(new Set());
  const [compatWarnings, setCompatWarnings] = useState([]);

  // Recheck screen compatibility whenever the selection changes
  useEffect(() => {
    if (selectedScreens.size === 0 || !jobId) {
      setCompatWarnings([]);
      return;
    }
    let cancelled = false;
    api.post('/api/equipment/check-compatibility', {
      dcp_job_id: parseInt(jobId),
      screen_ids: Array.from(selectedScreens),
    }).then(r => {
      if (!cancelled) setCompatWarnings(r.data?.warnings || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedScreens, jobId]);
  const [selectedTimezone, setSelectedTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  // Primary KDM inputs as of v10.17.15: operator types the screening
  // date(s) and the validity window is derived via the shared policy
  // (14-day-before / 2-day-after). The legacy datetime-local pickers
  // for validFrom/validTo are still kept around — they live behind the
  // "Override window" expander for festival / multi-week-residency cases
  // — but the day-to-day path can't make a month-typo bypass the policy
  // anymore because the operator never types those fields directly.
  const [screeningFirst, setScreeningFirst] = useState(offsetDateOnly(7));
  const [screeningLast, setScreeningLast] = useState(offsetDateOnly(7));
  const [overrideWindow, setOverrideWindow] = useState(false);
  const [validFrom, setValidFrom] = useState(offsetDate(0));
  const [validTo, setValidTo] = useState(offsetDate(14));
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // v10.17.30: physical deliveries for this DCP job, keyed by venue_id so
  // each venue row can surface its current shipping status + tracking.
  const [physicalDeliveries, setPhysicalDeliveries] = useState([]);

  useEffect(() => {
    dispatch(fetchDCPJobs({}));
    dispatch(fetchVenues());
    dispatch(fetchKDMRecords({ dcpJobId: parseInt(jobId) }));
  }, [dispatch, jobId]);

  // Pull physical deliveries for this DCP job so the venue list can show
  // shipping status (staged / shipped / delivered) + tracking number
  // inline. Same 5 s refresh cadence as KDM records — these are
  // mostly-static after Mark Shipped is clicked, but tracking edits and
  // status transitions still need to surface promptly.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get('/api/physical-delivery/deliveries', {
          params: { dcp_job_id: parseInt(jobId) },
        });
        if (!cancelled) setPhysicalDeliveries(data || []);
      } catch (_e) { /* ignore — page works without this data */ }
    };
    load();
    const interval = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [jobId]);

  // Latest non-archived delivery per venue. Multiple drives to one
  // venue are rare but possible (drive lost in transit, re-staged) —
  // the most recent one is the one with current status meaning.
  const deliveryByVenue = React.useMemo(() => {
    const m = new Map();
    for (const d of physicalDeliveries) {
      if (!d.venue_id) continue;
      const existing = m.get(d.venue_id);
      if (!existing || new Date(d.created_at || 0) > new Date(existing.created_at || 0)) {
        m.set(d.venue_id, d);
      }
    }
    return m;
  }, [physicalDeliveries]);

  // Pre-fill the screening date from the most recent submitted venue
  // request. The validity window is derived from the screening date via
  // the policy — operators no longer type valid_from/valid_to directly
  // on the happy path, which closes off the "06 instead of 05" typo
  // class that shipped a June KDM for a May screening (Harkins · May
  // 2026).
  useEffect(() => {
    (async () => {
      try {
        // Path is /request/links (not /api/request/links) — the
        // kdm_request router mounts at root because public form
        // endpoints (/request/{token}) sit alongside operator
        // endpoints. See sibling note in DCPDistributeDialog.jsx.
        const { data } = await api.get('/request/links');
        const submitted = (data || [])
          .filter(t => t.first_screening_at && t.last_screening_at)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        if (submitted.length === 0) return;
        const t = submitted[0];
        const pad = (n) => String(n).padStart(2, '0');
        const dateOf = (iso) => {
          const d = new Date(iso);
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        };
        setScreeningFirst(dateOf(t.first_screening_at));
        setScreeningLast(dateOf(t.last_screening_at));
      } catch (_e) { /* ignore */ }
    })();
  }, [jobId]);

  // Recompute the derived window whenever the screening date(s) change.
  // The result is shown as read-only text so the operator can sanity-
  // check before clicking Generate, but they can't edit the dates
  // directly unless they flip the "override window" expander.
  const derivedWindow = deriveWindow(screeningFirst, screeningLast);
  useEffect(() => {
    if (!overrideWindow && derivedWindow.validFrom && derivedWindow.validTo) {
      setValidFrom(derivedWindow.validFrom);
      setValidTo(derivedWindow.validTo);
    }
  }, [derivedWindow.validFrom, derivedWindow.validTo, overrideWindow]);

  // Refresh KDM records periodically
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchKDMRecords({ dcpJobId: parseInt(jobId) }));
    }, 5000);
    return () => clearInterval(interval);
  }, [dispatch, jobId]);

  const job = jobs.find(j => j.id === parseInt(jobId));
  const jobKdms = records.filter(r => r.dcp_job_id === parseInt(jobId));

  // Build flat list of screens with certs
  const allScreens = venues.flatMap(v =>
    (v.screens || []).filter(s => s.is_active && s.certificate_filename).map(s => ({
      ...s,
      venue: v,
      venueDisplay: `${v.chain ? v.chain + ' ' : ''}${v.name}`,
    }))
  );

  // Which screens already have KDMs for this DCP
  const screensWithKdm = new Set(jobKdms.map(r => r.screen_id).filter(Boolean));

  const toggleScreen = (screenId) => {
    setSelectedScreens(prev => {
      const next = new Set(prev);
      if (next.has(screenId)) next.delete(screenId);
      else {
        next.add(screenId);
        // Auto-set timezone from venue if this is the first selection
        if (prev.size === 0) {
          const screen = allScreens.find(s => s.id === screenId);
          if (screen?.venue?.timezone) setSelectedTimezone(screen.venue.timezone);
        }
      }
      return next;
    });
  };

  const selectAllAvailable = () => {
    const available = allScreens.filter(s => !screensWithKdm.has(s.id)).map(s => s.id);
    setSelectedScreens(new Set(available));
  };

  const selectNone = () => setSelectedScreens(new Set());

  const handleGenerate = async () => {
    if (selectedScreens.size === 0) {
      setError('Select at least one screen');
      return;
    }
    setGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      // Send the screening dates as the primary signal so the server
      // can apply the policy. If the operator opened the override
      // expander, also send the literal valid_from/valid_to and let
      // the server soft-check coverage.
      const payload = {
        dcp_job_id: parseInt(jobId),
        screen_ids: Array.from(selectedScreens),
        notes: notes || undefined,
        timezone: selectedTimezone,
      };
      if (screeningFirst) {
        payload.screening_first = new Date(`${screeningFirst}T00:00:00`).toISOString();
        payload.screening_last = new Date(`${screeningLast || screeningFirst}T23:59:00`).toISOString();
      }
      if (overrideWindow) {
        payload.valid_from = new Date(validFrom).toISOString();
        payload.valid_to = new Date(validTo).toISOString();
        payload.force_window = true;
      }
      const result = await dispatch(generateBulkKDM(payload)).unwrap();
      setSuccess(`Generated ${result.length} KDM(s)`);
      setSelectedScreens(new Set());
      dispatch(fetchKDMRecords({ dcpJobId: parseInt(jobId) }));
    } catch (err) {
      setError(typeof err === 'string' ? err : 'KDM generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const getKdmStatus = (screenId) => {
    const kdm = jobKdms.find(r => r.screen_id === screenId);
    if (!kdm) return null;
    return kdm;
  };

  const kdmStatusIcon = (status) => {
    if (status === 'completed') return <DoneIcon fontSize="small" color="success" />;
    if (status === 'generating') return <CircularProgress size={16} />;
    if (status === 'failed') return <FailIcon fontSize="small" color="error" />;
    return <PendingIcon fontSize="small" color="action" />;
  };

  // Group venues by chain
  const chains = {};
  const independents = [];
  venues.forEach(v => {
    const screensWithCerts = (v.screens || []).filter(s => s.is_active && s.certificate_filename);
    if (screensWithCerts.length === 0) return;
    if (v.chain) {
      if (!chains[v.chain]) chains[v.chain] = [];
      chains[v.chain].push(v);
    } else {
      independents.push(v);
    }
  });

  if (!job) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/dcp')}>Back to DCP Queue</Button>
        <Typography sx={{ mt: 2 }}>Loading DCP job...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Tooltip title="Back to DCP Queue">
          <IconButton onClick={() => navigate('/dcp')}><BackIcon /></IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Distribute: {job.name}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>#{job.id}</Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {job.resolution} {job.container} — {job.encrypt ? 'Encrypted' : 'Open'}
            {job.output_path && <Typography component="span" variant="caption" sx={{ ml: 1, fontFamily: 'monospace' }}>{job.output_path.split('/').pop()}</Typography>}
          </Typography>
        </Box>
        <Tooltip title="Refresh KDM status">
          <IconButton onClick={() => dispatch(fetchKDMRecords({ dcpJobId: parseInt(jobId) }))}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {allScreens.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button size="small" onClick={() => navigate('/venues')}>Go to Venues</Button>}>
          No screens with certificates in the venue catalog. Add venues and upload certificates in Venue Catalog.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: venue/screen selection */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Select Screens</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" startIcon={<SelectAllIcon />} onClick={selectAllAvailable}>
                  Select All Available
                </Button>
                <Button size="small" onClick={selectNone}>Clear</Button>
              </Box>
            </Box>

            {/* Venue groups */}
            {Object.entries(chains).map(([chainName, chainVenues]) => (
              <Accordion key={chainName} defaultExpanded disableGutters sx={{ mb: 0.5, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                  <Typography variant="subtitle2" fontWeight={600}>{chainName}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {chainVenues.map(v => <VenueScreenList key={v.id} venue={v} selectedScreens={selectedScreens} onToggle={toggleScreen} getKdmStatus={getKdmStatus} kdmStatusIcon={kdmStatusIcon} physicalDelivery={deliveryByVenue.get(v.id)} />)}
                </AccordionDetails>
              </Accordion>
            ))}

            {independents.map(v => (
              <VenueScreenList key={v.id} venue={v} selectedScreens={selectedScreens} onToggle={toggleScreen} getKdmStatus={getKdmStatus} kdmStatusIcon={kdmStatusIcon} physicalDelivery={deliveryByVenue.get(v.id)} />
            ))}
          </Paper>

          {/* KDM History for this DCP */}
          {jobKdms.length > 0 && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                KDM History
                <Chip label={jobKdms.length} size="small" sx={{ ml: 1 }} />
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Venue</TableCell>
                      <TableCell>Screen</TableCell>
                      <TableCell>Validity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobKdms.map(record => (
                      <TableRow key={record.id} hover>
                        <TableCell>{record.venue_name || '—'}</TableCell>
                        <TableCell>{record.screen_name || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(record.valid_from).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} — {new Date(record.valid_to).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {kdmStatusIcon(record.status)}
                            <Typography variant="caption">{record.status}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Download KDM">
                            <span>
                              <IconButton size="small" disabled={record.status !== 'completed'} onClick={() => dispatch(downloadKDM(record.id))}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={record.tech_contact_email ? `Email to ${record.tech_contact_email}` : 'No tech contact email'}>
                            <span>
                              <IconButton size="small" disabled={record.status !== 'completed' || !record.tech_contact_email} onClick={async () => {
                                try {
                                  await dispatch(emailKDM(record.id)).unwrap();
                                  setSnackbar({ open: true, message: `KDM emailed to ${record.tech_contact_email}`, severity: 'success' });
                                } catch (err) {
                                  setSnackbar({ open: true, message: typeof err === 'string' ? err : 'Failed to send email', severity: 'error' });
                                }
                              }}>
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete KDM">
                            <IconButton size="small" onClick={() => setDeleteConfirm(record)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Delete confirmation */}
          <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
            <DialogTitle>Delete KDM Record?</DialogTitle>
            <DialogContent>
              <Typography>
                Delete KDM for {deleteConfirm?.venue_name} — {deleteConfirm?.screen_name}? This cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={async () => {
                try {
                  await dispatch(deleteKDMRecord(deleteConfirm.id)).unwrap();
                  setSnackbar({ open: true, message: 'KDM record deleted', severity: 'success' });
                } catch (err) {
                  setSnackbar({ open: true, message: typeof err === 'string' ? err : 'Failed to delete', severity: 'error' });
                }
                setDeleteConfirm(null);
              }}>Delete</Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
          </Snackbar>
        </Grid>

        {/* Right: validity + generate */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, position: 'sticky', top: 80 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              <KDMIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Generate KDMs
            </Typography>

            <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{selectedScreens.size}</strong> screen{selectedScreens.size !== 1 ? 's' : ''} selected
              </Typography>
              {screensWithKdm.size > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {screensWithKdm.size} already have KDMs for this title
                </Typography>
              )}
            </Box>

            {compatWarnings.length > 0 && (
              <Alert
                severity={compatWarnings.some(w => w.issues.some(i => i.severity === 'error')) ? 'error' : 'warning'}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Compatibility issues on {compatWarnings.length} screen{compatWarnings.length !== 1 ? 's' : ''}
                </Typography>
                {compatWarnings.map(w => (
                  <Box key={w.screen_id} sx={{ mt: 0.5 }}>
                    <Typography variant="caption" fontWeight={600}>
                      {w.venue_name} — {w.screen_name}
                    </Typography>
                    {w.issues.map((i, idx) => (
                      <Typography key={idx} variant="caption" component="div" sx={{ ml: 1, mt: 0.25 }}>
                        <strong>{i.severity === 'error' ? '✗' : '⚠'}</strong> {i.title}: {i.detail}
                      </Typography>
                    ))}
                  </Box>
                ))}
                <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                  {compatWarnings.some(w => w.issues.some(i => i.severity === 'error'))
                    ? 'KDMs will still be generated, but the venue may not be able to play this DCP. Consider an alternate version or remove the affected screens.'
                    : 'KDMs will still be generated. These are soft preferences — playback should work.'}
                </Typography>
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Validity Window
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              24-48h before first playback, 12-24h after last screening.
            </Typography>

            <Autocomplete
              value={TIMEZONE_OPTIONS.find(o => o.value === selectedTimezone) || TIMEZONE_OPTIONS[0]}
              onChange={(e, v) => v && setSelectedTimezone(v.value)}
              options={TIMEZONE_OPTIONS}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              size="small"
              renderInput={(params) => <TextField {...params} label="Venue Timezone" />}
              sx={{ mb: 2 }}
              disableClearable
            />

            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <TextField
                label="First screening"
                type="date"
                value={screeningFirst}
                onChange={(e) => setScreeningFirst(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Last screening"
                type="date"
                value={screeningLast}
                onChange={(e) => setScreeningLast(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                helperText="Same as first for a single-night show"
              />
            </Box>
            <Box
              sx={{
                p: 1.5, mb: 1.5,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                KDM window (derived: 14 days before first screening through 2 days after last)
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                {derivedWindow.validFrom || '—'} → {derivedWindow.validTo || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Time anchored to {selectedTimezone}
              </Typography>
            </Box>
            <Accordion
              expanded={overrideWindow}
              onChange={(_e, exp) => setOverrideWindow(exp)}
              elevation={0}
              sx={{ mb: 2, bgcolor: 'transparent', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 32 }}>
                <Typography variant="caption" color="text.secondary">
                  Advanced: override the derived KDM window
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Use for festival residencies / multi-week runs. The server will refuse the
                  override unless it actually covers the screening date(s) above (force-window).
                </Typography>
                <TextField
                  label="Valid From"
                  type="datetime-local"
                  fullWidth
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={`Time in ${selectedTimezone}`}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  label="Valid To"
                  type="datetime-local"
                  fullWidth
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={`Time in ${selectedTimezone}`}
                />
              </AccordionDetails>
            </Accordion>
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Opening weekend, 12 screens"
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <KDMIcon />}
              onClick={handleGenerate}
              disabled={generating || selectedScreens.size === 0}
            >
              {generating ? 'Generating...' : `Generate ${selectedScreens.size} KDM${selectedScreens.size !== 1 ? 's' : ''}`}
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function VenueScreenList({ venue, selectedScreens, onToggle, getKdmStatus, kdmStatusIcon, physicalDelivery }) {
  const theme = useTheme();
  const screens = (venue.screens || []).filter(s => s.is_active && s.certificate_filename);
  if (screens.length === 0) return null;

  // v10.17.30: per-venue physical-delivery indicator. v10.18.17: moved
  // from a small inline chip next to the venue name to a dedicated
  // delivery-status row below the venue header — easy to miss when
  // it was inline, and the operator's primary question on this page
  // is "is this venue digital or physical?" The full lifecycle
  // (shipped → delivered → received → tested) is shown as a
  // progression of chips so the reader can tell at a glance how far
  // along the drive is.
  const d = physicalDelivery;

  const lifecycleChips = d ? (() => {
    const trk = d.tracking_number;
    const carrier = d.carrier || (trk ? 'FedEx' : null);
    const carrierTrk = [carrier, trk].filter(Boolean).join(' ');
    // status → chip rendering. Highlights the current state and
    // surfaces context-specific detail (POD signature, tracking
    // number) when available.
    // v12.0.0: shared wording (utils/physicalDelivery) so this chip,
    // the Distribution card, and the operator Physical Drives page say
    // the same thing, including whether the KDM is on the drive.
    const status = d.status;
    const primary = physicalStatus(d);
    const primaryColor = primary.color;
    let primaryLabel = driveSummaryLabel(d);
    if (status === 'shipped' && carrierTrk) {
      primaryLabel = `Shipped · ${carrierTrk}`;
    } else if (status === 'delivered') {
      if (d.signed_for_by_name) primaryLabel = `Delivered · signed by ${d.signed_for_by_name}`;
      else if (carrierTrk) primaryLabel = `Delivered · ${carrierTrk}`;
      else primaryLabel = 'Delivered';
    }

    return (
      <Box sx={{
        mt: 0.5, mb: 0.5, ml: 4, p: 1,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      }}>
        <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: 0.5, textTransform: 'uppercase', color: theme.palette.primary.main }}>
          Physical Delivery
        </Typography>
        <Tooltip title={
          `Drive #${d.id}` +
          (d.size_bytes ? ` · ${(d.size_bytes / (1 << 30)).toFixed(1)} GB` : '') +
          (carrier ? ` · ${carrier}` : '') +
          (trk ? ` · ${trk}` : '') +
          (d.shipped_at ? ` · shipped ${new Date(d.shipped_at).toLocaleDateString()}` : '') +
          (d.delivered_at ? ` · delivered ${new Date(d.delivered_at).toLocaleDateString()}` : '') +
          (d.signed_for_by_name ? ` · signed by ${d.signed_for_by_name}` : '') +
          (d.received_at ? ` · received ${new Date(d.received_at).toLocaleDateString()}` : '')
        }>
          <Chip
            size="small" color={primaryColor} label={primaryLabel}
            sx={{ fontWeight: 600, cursor: trk ? 'pointer' : 'default' }}
            onClick={trk ? () => { navigator.clipboard?.writeText(trk); } : undefined}
          />
        </Tooltip>
        {/* Post-delivery progression chips — show as small follow-ons. */}
        {d.received_at && (
          <Chip size="small" color="success" variant="outlined"
                label={d.received_by_name ? `Received · ${d.received_by_name}` : 'Received'}
                sx={{ fontSize: '0.7rem' }} />
        )}
        {d.tested_at && (
          <Chip size="small" color="success" variant="outlined"
                label={d.tested_by_name ? `Tested · ${d.tested_by_name}` : 'Tested'}
                sx={{ fontSize: '0.7rem' }} />
        )}
      </Box>
    );
  })() : null;

  return (
    <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <TheaterIcon fontSize="small" color="action" />
        <Typography variant="body2" fontWeight={600}>
          {venue.chain ? `${venue.chain} ` : ''}{venue.name}
        </Typography>
        {venue.city && <Typography variant="caption" color="text.secondary">— {venue.city}{venue.state ? `, ${venue.state}` : ''}</Typography>}
      </Box>
      {lifecycleChips}
      {screens.map(screen => {
        const kdm = getKdmStatus(screen.id);
        const hasKdm = Boolean(kdm);
        return (
          <Box key={screen.id} sx={{ display: 'flex', alignItems: 'center', pl: 4, py: 0.25 }}>
            <Checkbox
              size="small"
              checked={selectedScreens.has(screen.id)}
              onChange={() => onToggle(screen.id)}
              disabled={hasKdm && kdm.status === 'completed'}
              sx={{ p: 0.5, mr: 1 }}
            />
            <ScreenIcon fontSize="small" sx={{ mr: 0.5, color: 'action.active' }} />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {screen.name}
              {screen.screen_type && <Chip label={screen.screen_type} size="small" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />}
            </Typography>
            {screen.projector_make && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                {screen.projector_make} {screen.projector_model || ''}
              </Typography>
            )}
            {hasKdm && (
              <Tooltip title={`KDM ${kdm.status}: ${new Date(kdm.valid_from).toLocaleDateString()} — ${new Date(kdm.valid_to).toLocaleDateString()}`}>
                {kdmStatusIcon(kdm.status)}
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default DCPDistribute;
